/**
 * React adapter for the framework-agnostic i18n runtime.
 *
 * Subscribes the component to locale changes so calling `setLocale()`
 * anywhere in the tree re-renders consumers.
 */
import { useCallback, useEffect, useState } from "react";
import {
    getLocale,
    setLocale as setLocaleRuntime,
    subscribe,
    supportedLocales,
    t as translateRuntime,
    type Locale,
} from "../i18n";
import type { MessageKey } from "../locales/en";

export interface UseTranslationResult {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: MessageKey) => string;
  supportedLocales: readonly Locale[];
}

export function useTranslation(): UseTranslationResult {
  const [locale, setLocaleState] = useState<Locale>(() => getLocale());

  useEffect(() => {
    return subscribe((next) => setLocaleState(next));
  }, []);

  const setLocale = useCallback((next: Locale) => {
    setLocaleRuntime(next);
  }, []);

  const t = useCallback(
    (key: MessageKey) => translateRuntime(key, locale),
    [locale],
  );

  return { locale, setLocale, t, supportedLocales };
}
